import React, {useEffect, useRef, useState} from 'react';

// Components
import {DashboardWrapper} from '../../../components/DashboardWrapper/DashboardWrapper.styles';
import {FaArrowLeft, FaRegTrashAlt, FaUpload} from "react-icons/fa";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {IconContext} from "react-icons";
import {Header, ImageField, Images} from "../styles";
import CircleButton from "../../../widgets/AddIcon";
import {
    deletePatientFile,
    getLessionDetector,
    getPatientFilesList,
    postLessionDetector,
    postWirnklesAnalysis,
    saveImageAnalysis
} from "../../../utils/api-lists";
import toast from "react-hot-toast";
import {filterFileName} from "../../../utils/general.util";
import {FaXmark} from "react-icons/fa6";
import {DivRow} from "../../../widgets/GeneralBody";
import {useTranslation} from 'react-i18next';
import {Button, Carousel, Divider, Modal, Popover, Progress, Spin} from "antd";
import {MdCompare} from "react-icons/md";
import styled from "styled-components";
import EyeWrinkle from "../../../assets/wrinkles/eyewrinkle.png";
import LipWrinkle from "../../../assets/wrinkles/lipwrinkle.png";
import ForeheadWrinkle from "../../../assets/wrinkles/wrinkle.png";
import './filemanagement.style.css';
import PlaceholderView from '../components/PlaceholderView';
import PhotosMedia from '../../../assets/visual_analysis.png';
import FaceAnalyzingAnimComponent from "../../../components/FaceAnalyzingAnim/face-analyzing-anim.component";


const PatientsCabinetPage = () => {
    const {patientId} = useParams();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [files, setFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [isCompare, setIsCompare] = useState(false);
    const [filesToCompare, setFilesToCompare] = useState([]);
    const {t} = useTranslation("common");
    const imageRef = useRef(null);

    // Wrinkles analysis states
    const [isWrinklesModalOpen, setIsWrinklesModalOpen] = useState(false);
    const [wrinklesAnalysisLoading, setWrinklesAnalysisLoading] = useState(false);
    const [wrinklesAnalysisResult, setWrinklesAnalysisResult] = useState(null);
    const [flipImage, setFlipImage] = useState(false);

    // New states for skin lesion analysis
    const [isSkinLesionModalOpen, setIsSkinLesionModalOpen] = useState(false);
    const [skinLesionAnalysisLoading, setSkinLesionAnalysisLoading] = useState(false);
    const [skinLesionAnalysisResult, setSkinLesionAnalysisResult] = useState(null);
    const [showDetections, setShowDetections] = useState(true);
    const [highlightedDetection, setHighlightedDetection] = useState(null);

    const [imageDimensions, setImageDimensions] = useState({width: 0, height: 0, originalWidth: 0, originalHeight: 0});
    const {state} = useLocation();
    const [currentProcess, setCurrentProcess] = useState(null);
    const [allLoading, setAllLoading] = useState(false);
    const [isAllModalOpen, setIsAllModalOpen] = useState(false);
    const slider = useRef(null);

    const toggleAllModal = () => {
        setIsAllModalOpen(!isAllModalOpen);
    }

    function toggleModal(file) {
        setSelectedFile(file);
        setIsOpen(!isOpen);
    }

    function toggleWrinklesModal() {
        setIsWrinklesModalOpen(!isWrinklesModalOpen);
    }

    function toggleSkinLesionModal() {
        setIsSkinLesionModalOpen(!isSkinLesionModalOpen);
    }

    function getPatientFiles() {
        if (isLoading) return;
        setIsLoading(true);
        getPatientFilesList(patientId)
            .then((r) => {
                // Sort files by upload_date (newest first), with null dates at the end
                const sortedFiles = [...r.data].sort((a, b) => {
                    // If either date is null, put it at the end
                    if (!a.upload_date && !b.upload_date) return 0;
                    if (!a.upload_date) return 1; // a goes after b
                    if (!b.upload_date) return -1; // a goes before b

                    // Both dates exist, compare normally
                    const dateA = new Date(a.upload_date).getTime();
                    const dateB = new Date(b.upload_date).getTime();
                    return dateB - dateA; // Descending order (newest first)
                });

                setFiles(sortedFiles);
                console.log('state', state);
                if (state?.file_open_last) {
                    const fileToOpen = sortedFiles[0]; // Get newest file
                    if (fileToOpen) {
                        setSelectedFile(fileToOpen);
                        setIsOpen(true);
                    }
                }
            })
            .catch((e) => {
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    function deleteFile() {
        if (isLoading) return;
        setIsLoading(true);
        toast.promise(
            deletePatientFile(patientId, selectedFile['id']), {
                loading: t("loading_delete"),
                success: t("success_delete"),
                error: (e) => `${t("delete_failed")}: ${e.response.data.message}`
            }
        )
            .catch(e => {
                console.error(e);
            })
            .finally(() => {
                setIsOpen(false);
                setIsLoading(false);
                getPatientFiles();
            })
    }

    const convertImageToBase64 = (imageFile) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(imageFile);
            reader.onloadend = () => {
                resolve(reader.result.split(',')[1]); // Get the Base64 string without the prefix
            };
            reader.onerror = (error) => {
                reject(error);
            };
        });
    };

    function extractUuidFromUrl(url) {
        // Regular expression to match UUID pattern in the URL
        const uuidPattern = /([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i;

        // Extract the UUID using regex
        const match = url.match(uuidPattern);

        // Return the UUID if found, otherwise return null
        return match ? match[1] : null;
    }

    function extractUuidFromUrlAcne(url) {
        return url.split('/').pop();
    }

    async function analyzeAll() {
        if (allLoading) return;
        setAllLoading(true);
        setIsAllModalOpen(true);
        setCurrentProcess('wrinkles');
        await new Promise(resolve => setTimeout(resolve, 2000));
        await performWrinklesAnalysis(true);
        setCurrentProcess('skin_lesions');
        await new Promise(resolve => setTimeout(resolve, 2000));
        await performSkinLesionAnalysis(true);
        setAllLoading(false);
    }

    async function performWrinklesAnalysis(isAll = false) {
        if (wrinklesAnalysisLoading || !selectedFile) return;

        setWrinklesAnalysisLoading(true);
        if (!isAll) {
            setIsWrinklesModalOpen(true);
        }

        try {
            const fetchImageResponse = await fetch(selectedFile['url'], {
                headers: {
                    'Access-Control-Allow-Origin': '*'
                }
            });
            const imageBlob = await fetchImageResponse.blob();

            const imageFile = new File([imageBlob], selectedFile['image_name'], {
                type: fetchImageResponse.headers.get('content-type') || 'image/jpeg'
            });
            const base64Image = await convertImageToBase64(imageFile);

            const response = await toast.promise(
                postWirnklesAnalysis(base64Image), {
                    loading: t('analyzing_wrinkles'),
                    success: t('analysis_complete'),
                    error: (e) => `${t('analysis_failed')}: ${e.response?.data?.message || t('unknown_error')}`
                }
            );
            const analysisId = response.data?.analysis_id ?? extractUuidFromUrl(response.data?.cloud_upload?.cloud_url ?? '');

            if (response.data.analysis_id) {

                if (process.env.REACT_APP_ENV !== 'development') {
                    await toast.promise(
                        saveImageAnalysis({
                            patient_id: patientId,
                            analysis_id: analysisId,
                            image_id: selectedFile['id'],
                        }),
                        {
                            loading: t('saving_analysis'),
                            success: t('analysis_saved'),
                            error: (e) => `${t('save_failed')}: ${e.response?.data?.message || t('unknown_error')}`
                        }
                    );
                }
            }

            setWrinklesAnalysisResult(response.data);
        } catch (e) {
            console.error('Error performing wrinkles analysis:', e);
        } finally {
            setWrinklesAnalysisLoading(false);
        }
    }

    // New function for skin lesion analysis
    async function performSkinLesionAnalysis(isAll = false) {
        if (skinLesionAnalysisLoading || !selectedFile) return;

        setSkinLesionAnalysisLoading(true);
        if (!isAll) {
            setIsSkinLesionModalOpen(true);
        }

        try {
            const fetchImageResponse = await fetch(selectedFile['url'], {
                headers: {
                    'Access-Control-Allow-Origin': '*'
                }
            });
            const imageBlob = await fetchImageResponse.blob();

            const imageFile = new File([imageBlob], selectedFile['image_name'], {
                type: fetchImageResponse.headers.get('content-type') || 'image/jpeg'
            });

            // Create a temporary image to check dimensions
            const img = new Image();
            const imageUrl = URL.createObjectURL(imageBlob);

            // Create a promise to handle the image loading
            const loadImagePromise = new Promise((resolve, reject) => {
                img.onload = () => resolve();
                img.onerror = () => reject(new Error("Failed to load image"));
                img.src = imageUrl;
            });

            await loadImagePromise;

            // Check if resizing is needed
            let base64Image;
            // if (img.width < 512 || img.height < 512) {
            //     // Resize the image to at least 512px on smallest dimension
            //     const canvas = document.createElement('canvas');
            //     let newWidth = img.width;
            //     let newHeight = img.height;
            //
            //     // Calculate new dimensions while maintaining aspect ratio
            //     if (img.width < img.height) {
            //         newWidth = 512;
            //         newHeight = Math.floor(img.height * (512 / img.width));
            //     } else {
            //         newHeight = 512;
            //         newWidth = Math.floor(img.width * (512 / img.height));
            //     }
            //
            //     canvas.width = newWidth;
            //     canvas.height = newHeight;
            //
            //     const ctx = canvas.getContext('2d');
            //     ctx.drawImage(img, 0, 0, newWidth, newHeight);
            //
            //     // Convert canvas to base64
            //     base64Image = canvas.toDataURL(imageFile.type).split(',')[1];
            //
            //     console.log(`Image resized from ${img.width}x${img.height} to ${newWidth}x${newHeight}`);
            // } else {
            // No resizing needed
            base64Image = await convertImageToBase64(imageFile);
            // }

            // Clean up the URL object
            URL.revokeObjectURL(imageUrl);

            const detectResponse = await postLessionDetector(base64Image);
            let response = {};
            if (detectResponse.data?.task_id) {
                const taskId = detectResponse.data?.task_id;
                let i = 1;
                let last = 1;
                do {
                    await new Promise(resolve => setTimeout(resolve, i * 1000));
                    response = await getLessionDetector(taskId);
                    i += 1;
                } while (response.data?.state === 'STARTED')
            }

            response = {
                data: response.data.result
            };

            const analysisId = response.data?.analysis_id ?? extractUuidFromUrlAcne(response.data?.cloud_upload?.folder ?? '');
            console.log('Analysis ID:', analysisId);
            if (analysisId) {
                if (window.location.hostname !== 'localhost') {
                    await toast.promise(
                        saveImageAnalysis({
                            patient_id: patientId,
                            analysis_id: analysisId,
                            analysis_type: 2,
                            image_id: selectedFile['id'],
                        }),
                        {
                            loading: t('saving_analysis'),
                            success: t('analysis_saved'),
                            error: (e) => `${t('save_failed')}`
                        }
                    );
                }
            }

            setSkinLesionAnalysisResult(response.data);
        } catch (e) {
            console.error('Error performing skin lesion analysis:', e);
        } finally {
            if (window.location.hostname === 'localhost') {
                setTimeout(
                    () => {
                        setSkinLesionAnalysisLoading(false);
                    },
                    2000
                )
            } else {
                setSkinLesionAnalysisLoading(false);
            }
        }
    }

    function compareFiles() {
        console.log(filesToCompare);
        if (isCompare) {
            navigate({
                pathname: `compare`,
                search: `?file1Id=${filesToCompare[0]}&file2Id=${selectedFile['id']}`
            });
            return;
        }

        setFilesToCompare([...filesToCompare, selectedFile['id']]);
        setIsCompare(true);
        setIsOpen(false);
    }

    function clearCompare() {
        setSelectedFile([]);
        setIsCompare(false);
    }

    useEffect(() => {
        document.title = 'Visual analysis';
    }, []);

    useEffect(() => {
        getPatientFiles();
    }, [patientId]);


    useEffect(() => {
        if (imageRef.current) {
            const updateDimensions = () => {
                setImageDimensions({
                    width: imageRef.current.clientWidth,
                    height: imageRef.current.clientHeight,
                    originalWidth: imageRef.current.naturalWidth,
                    originalHeight: imageRef.current.naturalHeight
                });
            };

            // Set initial dimensions when image loads
            if (imageRef.current.complete) {
                updateDimensions();
            } else {
                imageRef.current.onload = updateDimensions;
            }

            // Add resize listener to handle container size changes
            const resizeObserver = new ResizeObserver(updateDimensions);
            resizeObserver.observe(imageRef.current);

            return () => {
                if (imageRef.current) {
                    resizeObserver.unobserve(imageRef.current);
                }
            };
        }
    }, [imageRef.current]);

    const ImageWrapper = ({file}) => {
        return (
            <StyledImageField onClick={() => toggleModal(file)}>
                <div className="image-container">
                    <img src={file['thumbnail_url']} alt="Image"/>
                    <div className="overlay">
                        <div className="overlay-content">
                            <span className="view-text">{t("view") || "View"}</span>
                        </div>
                    </div>
                </div>
                <div className="file-info">
                    <p className="file-name">{filterFileName(file['image_name'])}</p>
                    {file['upload_date'] && (
                        <p className="file-date">
                            {new Date(file['upload_date']).toLocaleDateString()}
                        </p>
                    )}
                </div>
            </StyledImageField>
        )
    }

    const VideoWrapper = ({file}) => {
        return (
            <StyledImageField onClick={() => toggleModal(file)}>
                <div className="image-container video-container">
                    <video playsInline={true} key={file['id']} muted>
                        <source src={file['url']} type="video/mp4"/>
                        {t("no_video_support")}
                    </video>
                    <div className="overlay">
                        <div className="overlay-content">
                            <span className="play-icon">▶</span>
                            <span className="view-text">{t("play") || "Play"}</span>
                        </div>
                    </div>
                    <div className="video-badge">
                        <span>VIDEO</span>
                    </div>
                </div>
                <div className="file-info">
                    <p className="file-name">{filterFileName(file['image_name'])}</p>
                    {file['upload_date'] && (
                        <p className="file-date">
                            {new Date(file['upload_date']).toLocaleDateString()}
                        </p>
                    )}
                </div>
            </StyledImageField>
        )
    }

    const ShowFiles = () => {
        return (
            files.map((file, index) => {
                switch (file['type']) {
                    case 'video':
                        return <VideoWrapper key={file.id || index} file={file}/>;
                    default:
                    case 'image':
                        return <ImageWrapper key={file.id || index} file={file}/>;
                }
            })
        )
    }

    const showWithFileType = () => {
        switch (selectedFile['type']) {
            case 'video':
                return (
                    <video controls playsInline={true} key={selectedFile['url']}>
                        <source src={selectedFile['url']} type="video/mp4" key={selectedFile['url']}/>
                        {t("no_video_support")}
                    </video>
                );
            default:
            case 'image':
                return <img src={selectedFile['url']} alt="Image" key={selectedFile['url']}/>;
        }
    }

    const toggleFlip = () => {
        setFlipImage(!flipImage);
    }

    const toggleDetections = () => {
        setShowDetections(!showDetections);
    }

    // Render wrinkles analysis result
    const renderWrinklesAnalysisResult = () => {
        if (!wrinklesAnalysisResult) return null;

        const results = wrinklesAnalysisResult?.wrinkle_analysis ?? {};
        const mask = wrinklesAnalysisResult?.wrinkle_mask;

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {mask && (
                    <div className="text-center flex justify-between gap-3 rounded-xl overflow-hidden relative">

                        <img src={selectedFile['url']} alt="Image"
                             style={{objectFit: 'cover', width: '100%', height: '100%'}}/>
                        {

                            !flipImage && (<div
                                className="absolute top-0 left-0 w-full h-full"
                                style={{
                                    backgroundImage: `url(${mask})`,
                                    backgroundSize: '100% 100%',
                                    backgroundPosition: 'center',
                                    opacity: 0.7,
                                    mixBlendMode: 'screen',
                                    filter: 'hue-rotate(210deg) saturate(2)'
                                }}
                            />)
                        }
                        <Button type="primary" className="absolute left-3 top-3"
                                onClick={toggleFlip}>Flip</Button>
                    </div>
                )}

                <WrinkleWrapper className="flex flex-col gap-3 justify-between items-start bg-white p-3 rounded-xl">
                    <h3 className="font-bold text-center text-black">Wrinkles level</h3>
                    {WrinkleField({
                        icon: EyeWrinkle,
                        title: t("eye"),
                        area: results['crows_feet']['area'],
                        skeleton: results['crows_feet']['skeleton_length'],
                        perimeter: results['crows_feet']['perimeter'],
                        average: results['crows_feet']['avg_width'],
                    })}
                    {WrinkleField({
                        icon: LipWrinkle,
                        title: t("lips"),
                        area: results['lips']['area'],
                        skeleton: results['lips']['skeleton_length'],
                        perimeter: results['lips']['perimeter'],
                        average: results['lips']['avg_width'],
                    })}
                    {WrinkleField({
                        icon: ForeheadWrinkle,
                        title: t("forehead"),
                        area: results['forehead']['area'],
                        skeleton: results['forehead']['skeleton_length'],
                        perimeter: results['forehead']['perimeter'],
                        average: results['forehead']['avg_width'],
                    })}
                </WrinkleWrapper>
            </div>
        );
    };
    const getLesionColor = (className) => {
        const colors = {
            'atrophic-hypertrophic_scar': '#ff9800', // Orange
            'melasma-nevus': '#9c27b0',             // Purple
            'papule-pustule': '#f44336',            // Red
            'default': '#2196f3'                    // Blue
        };

        return colors[className] || colors.default;
    };

    const mapAcneDetail = (acneType) => {
        const acneDetails = {
            'open-closed_comedo': t("open_closed_comedo"),
            'papule-pustule': t("papule_pustule"),
            'nodule-cyst': t("nodule_cyst"),
            'severe-acne-lesions': t("severe_acne_lesions"),
            'melasma-nevus': t("melasma_nevus"),
            'atrophic-hypertrophic_scar': t("atrophic_hypertrophic_scar"),
        }

        return acneDetails[acneType] || t("unknown_acne_type");
    }


    // New function to render skin lesion analysis results
    const renderSkinLesionAnalysisResult = () => {
        if (!skinLesionAnalysisResult) return null;

        const detections = skinLesionAnalysisResult?.detections || [];
        const visualization = skinLesionAnalysisResult?.visualization;


        // Group detections by class
        const groupedDetections = detections.reduce((acc, detection) => {
            const className = detection.class_name;
            if (!acc[className]) {
                acc[className] = [];
            }
            acc[className].push(detection);
            return acc;
        }, {});

        const content = (
            <div>
                {Object.entries(groupedDetections).map(([className, items], idx) => (
                    <div className="flex justify-between items-center mb-2">
                        <p className="capitalize text-xs">{mapAcneDetail(className)}</p>
                        <div className="rounded-full flex justify-center items-center" style={{
                            backgroundColor: getTagColor(className),
                            width: '18px',
                            height: '18px',
                        }}>
                        </div>
                    </div>
                ))}
            </div>
        );

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-3">
                    <div className="text-center flex justify-between gap-3 rounded-xl overflow-hidden relative">
                        <Popover content={content} title="Details" className="absolute right-3 top-3"
                                 placement="bottomRight">
                            <Button type="primary">{t("see_details")}</Button>
                        </Popover>
                        {
                            showDetections && visualization ?
                                <img src={`data:image/jpeg;base64,${visualization}`}
                                     ref={imageRef}
                                     alt="Detection Visualization"
                                     style={{objectFit: 'cover', width: '100%', height: '100%'}}
                                /> :
                                <div>
                                    <img src={selectedFile['url']}
                                         ref={imageRef}
                                         alt="Original Image"
                                         style={{objectFit: 'cover', width: '100%', height: '100%'}}
                                         onLoad={() => {
                                             if (imageRef.current) {
                                                 setImageDimensions({
                                                     width: imageRef.current.clientWidth,
                                                     height: imageRef.current.clientHeight,
                                                     originalWidth: imageRef.current.naturalWidth,
                                                     originalHeight: imageRef.current.naturalHeight
                                                 });
                                             }
                                         }}
                                    />
                                    {renderDetectionOverlays()}
                                </div>
                        }
                    </div>

                    <Button type="primary"
                            onClick={toggleDetections}>{showDetections ? 'Hide Detections' : 'Show Detections'}</Button>
                </div>

                <LesionWrapper className="flex flex-col gap-3 justify-between items-start bg-white p-3 rounded-xl">
                    <h3 className="font-bold text-center text-black w-full">{t("skin_lesion_analysis")}</h3>

                    {Object.keys(groupedDetections).length > 0 ? (
                        <>
                            <div className="w-full">
                                <h4 className="font-semibold mb-2">{t("detected_lesions")}</h4>
                                <div className="m-3">
                                    {Object.entries(groupedDetections).map(([className, items], idx) => (
                                        <div className="flex justify-between items-center mb-2">
                                            <h3 className="capitalize">{mapAcneDetail(className)}</h3>
                                            <div className="rounded-full flex justify-center items-center" style={{
                                                backgroundColor: getTagColor(className),
                                                width: '50px',
                                                height: '50px',
                                            }}>
                                                <p style={{
                                                    color: "white",
                                                    fontSize: '24px',
                                                }}>{items.length}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-12">
                                    {Object.entries(groupedDetections).map(([className, items], idx) => {
                                        const avgConfidence = items.reduce((sum, item) => sum + item.score, 0) / items.length;
                                        return (<div className="flex flex-col items-center" key={idx}>
                                            <Progress className="light-progress" type="circle"
                                                      percent={(avgConfidence * 100).toFixed(1)}
                                                      strokeColor={getTagColor(className)}
                                                      format={(percent) => `${percent}`}/>
                                            <div className="text-center">
                                                <h3 className="capitalize">{mapAcneDetail(className)}</h3>
                                            </div>
                                        </div>)

                                    })}

                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="w-full text-center p-4 bg-gray-50 rounded-lg">
                            {t("no_lesions_detected")}
                        </div>
                    )}
                </LesionWrapper>
            </div>
        );
    };

    const renderPopoverContent = (detection) => {
        return (
            <div>
                <h4 className="capitalize font-medium mb-2">{detection.class_name.replace(/_/g, ' ')}</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <div>{t("confidence")}:</div>
                    <div>{(detection.score * 100).toFixed(1)}%</div>
                    <div>{t("width")}:</div>
                    <div>{(detection.bbox[2] - detection.bbox[0]).toFixed(1)}px</div>
                    <div>{t("height")}:</div>
                    <div>{(detection.bbox[3] - detection.bbox[1]).toFixed(1)}px</div>
                    <div>{t("location")}:</div>
                    <div>{t("x")}: {detection.bbox[0].toFixed(0)}, {t("y")}: {detection.bbox[1].toFixed(0)}</div>
                </div>
            </div>
        );
    };

    const renderDetectionOverlays = () => {
        if (!skinLesionAnalysisResult) return null;

        const detections = skinLesionAnalysisResult.detections || [];
        if (!detections.length || !imageRef.current) return null;

        // Calculate aspect ratios
        const originalAspectRatio = imageDimensions.originalWidth / imageDimensions.originalHeight;
        const displayAspectRatio = imageDimensions.width / imageDimensions.height;

        // Determine if we need to account for letterboxing due to objectFit: 'cover'
        let scaleX, scaleY, offsetX = 0, offsetY = 0;

        if (displayAspectRatio > originalAspectRatio) {
            // Width is constrained, height may overflow
            const displayHeightIfFull = imageDimensions.width / originalAspectRatio;
            scaleX = imageDimensions.width / imageDimensions.originalWidth;
            scaleY = displayHeightIfFull / imageDimensions.originalHeight;
            // Calculate vertical overflow that gets cropped
            offsetY = (displayHeightIfFull - imageDimensions.height) / 2;
        } else {
            // Height is constrained, width may overflow
            const displayWidthIfFull = imageDimensions.height * originalAspectRatio;
            scaleY = imageDimensions.height / imageDimensions.originalHeight;
            scaleX = displayWidthIfFull / imageDimensions.originalWidth;
            // Calculate horizontal overflow that gets cropped
            offsetX = (displayWidthIfFull - imageDimensions.width) / 2;
        }

        return detections.map((detection, index) => {
            // Scale coordinates accounting for crop
            const x = detection.bbox[0] * scaleX - offsetX;
            const y = detection.bbox[1] * scaleY - offsetY;
            const width = (detection.bbox[2] - detection.bbox[0]) * scaleX;
            const height = (detection.bbox[3] - detection.bbox[1]) * scaleY;

            // Don't render if outside visible area
            if (x + width < 0 || y + height < 0 ||
                x > imageDimensions.width || y > imageDimensions.height) {
                return null;
            }

            const color = getTagColor(detection.class_name);
            const isHighlighted = highlightedDetection === index;

            // Calculate circle dimensions
            const centerX = x + width / 2;
            const centerY = y + height / 2;
            const diagonal = Math.sqrt(width * width + height * height);
            // Use the larger dimension to ensure circle covers the whole detection
            const radius = diagonal / 2 + 5; // Add some padding

            return (
                <Popover
                    key={index}
                    content={renderPopoverContent(detection)}
                    title={`${t("detection")} #${index + 1}`}
                    trigger="hover"
                    placement="top"
                    open={isHighlighted}
                >
                    <div
                        style={{
                            position: 'absolute',
                            left: `${Math.max(0, centerX - radius)}px`,
                            top: `${Math.max(0, centerY - radius)}px`,
                            width: `${Math.min(radius * 2, imageDimensions.width - Math.max(0, centerX - radius))}px`,
                            height: `${Math.min(radius * 2, imageDimensions.height - Math.max(0, centerY - radius))}px`,
                            border: `1px solid ${color}`,
                            borderRadius: '50%',
                            backgroundColor: 'transparent',
                            zIndex: isHighlighted ? 9 : 4
                        }}
                        onMouseEnter={() => setHighlightedDetection(index)}
                        onMouseLeave={() => setHighlightedDetection(null)}
                        onClick={() => setHighlightedDetection(isHighlighted ? null : index)}
                    />
                </Popover>
            );
        })
    };

    // Helper function to get tag color based on lesion type
    const getTagColor = (className) => {
        const colors = {
            'atrophic-hypertrophic_scar': 'orange',
            'melasma-nevus': '#5acd4b',
            'papule-pustule': '#bd47a1',
            'open-closed_comedo': '#73e5d5',
            'default': 'blue'
        };

        return colors[className] || colors.default;
    };

    const WrinkleField = ({icon, title, area, skeleton, perimeter, average}) => {
        return (
            <div className="bg-[#f1f1f1] flex gap-3 justify-between rounded-xl p-3 w-full">
                <div className="shadow-xl bg-white rounded-xl flex flex-col items-center pb-2 relative"
                     style={{width: '5rem', height: '5rem'}}>
                    <img src={icon} alt="Wrinkle Icon" className="w-full"
                         style={{objectFit: 'cover', width: '3rem', height: '3rem'}}/>
                    <p>{title}</p>
                </div>
                <div className="flex flex-row gap-3 items-center w-full justify-evenly">
                    <div className="flex flex-col gap-2">
                        <p>{t("area")}: {area}mm<sup>2</sup></p>
                        <p>{t("perimeter")}: {perimeter}mm</p>
                    </div>

                    <Divider type="vertical" style={{height: "100%", borderColor: '#888888'}}/>

                    <div className="flex flex-col gap-2">
                        <p>{t("skeleton_length")}: {skeleton}mm</p>
                        <p>{t("avg_width")}: {average}mm</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <DashboardWrapper>
            <Header>
                <IconContext.Provider value={{style: {cursor: 'pointer'}}}>
                    <FaArrowLeft onClick={() => navigate(-1)}/>
                </IconContext.Provider>
                <h3>{t("visual_analysis")}</h3>

                {files.length > 0 && !isLoading && (
                    <div className="flex flex-row gap-3 items-center">
                        <Button
                            icon={<FaUpload/>}
                            onClick={() => {
                                navigate("file-upload");
                            }}
                        >
                            {t("upload_media")}
                        </Button>
                    </div>
                )}
            </Header>
            {
                files.length === 0 && !isLoading && (
                    <PlaceholderView
                        image={PhotosMedia}
                        title={t("no_media_files_yet")}
                        description={t("upload_photo_description")}
                        buttonText={t("upload_now")}
                        onButtonClick={() => navigate("file-upload")}
                    />)
            }
            {
                isCompare &&
                <DivRow maxWidth={320} onClick={clearCompare}>
                    <h4>{t("select_file_to_compare")}</h4>
                    <IconContext.Provider value={{style: {cursor: 'pointer'}}}>
                        <FaXmark/>
                    </IconContext.Provider>
                </DivRow>
            }
            <Images>
                {
                    ShowFiles()
                }
            </Images>

            {/* File details modal */}
            {
                selectedFile &&
                <Modal
                    title={null}
                    footer={null}
                    onCancel={toggleModal}
                    open={isOpen}
                    width="90%"
                    style={{ maxWidth: '1200px', top: 20 }}
                    bodyStyle={{ padding: 0 }}
                    closeIcon={
                        <div style={{
                            background: 'rgba(0, 0, 0, 0.5)',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                        }}>
                            ✕
                        </div>
                    }
                >
                    <StyledModalContent>
                        <div className="modal-media-container">
                            {selectedFile && showWithFileType({selectedFile})}
                        </div>
                        
                        <div className="modal-info-section">
                            <div className="modal-actions">
                                {selectedFile['type'] === 'image' && (
                                    <Button 
                                        type="primary" 
                                        size="large"
                                        onClick={analyzeAll}
                                        className="analyze-btn"
                                    >
                                        {t("analyse_all")}
                                    </Button>
                                )}

                                <div className="action-buttons">
                                    <Button 
                                        icon={<MdCompare size={20} />}
                                        size="large"
                                        onClick={compareFiles}
                                    >
                                        {t("compare") || "Compare"}
                                    </Button>
                                    <Button 
                                        icon={<FaRegTrashAlt />}
                                        size="large"
                                        danger
                                        onClick={() => deleteFile()} 
                                        disabled={isLoading}
                                    >
                                        {t("delete") || "Delete"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </StyledModalContent>
                </Modal>
            }

            {/* Wrinkles analysis results modal */}
            <Modal
                title={t("wrinkles_analysis_results")}
                footer={null}
                open={isWrinklesModalOpen}
                onCancel={toggleWrinklesModal}
                width="90%"
                style={{ maxWidth: '1000px', top: '5vh' }}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', padding: '16px' }}
                centered={false}>
                {wrinklesAnalysisLoading ? (
                    <div className="flex justify-center items-center" style={{minHeight: '300px'}}>
                        <FaceAnalyzingAnimComponent
                            image_url={selectedFile['url']}
                            process_name="wrinkles"
                        />
                    </div>
                ) : (
                    renderWrinklesAnalysisResult()
                )}
                {!wrinklesAnalysisLoading && (
                    <div className="flex justify-end mt-4">
                        <Button type="primary" onClick={toggleWrinklesModal}>
                            {t("close")}
                        </Button>
                    </div>
                )}
            </Modal>

            {/* Skin Lesion analysis results modal */}
            <Modal
                title={t("skin_lesion_analysis_results") || "Skin Lesion Analysis Results"}
                footer={null}
                open={isSkinLesionModalOpen}
                onCancel={toggleSkinLesionModal}
                width="90%"
                style={{ maxWidth: '1000px', top: '5vh' }}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', padding: '16px' }}
                centered={false}>
                {skinLesionAnalysisLoading ? (
                    <div className="flex justify-center items-center" style={{minHeight: '300px'}}>
                        <FaceAnalyzingAnimComponent
                            image_url={selectedFile['url']}
                            process_name="skin_lesions"
                        />
                    </div>
                ) : (
                    renderSkinLesionAnalysisResult()
                )}
                {!skinLesionAnalysisLoading && (
                    <div className="flex justify-end mt-4">
                        <Button type="primary" onClick={toggleSkinLesionModal}>
                            {t("close") || "Close"}
                        </Button>
                    </div>
                )}
            </Modal>

            {/* All analysis modal */}
            <Modal
                title={t("all_analysis_results")}
                footer={null}
                open={isAllModalOpen}
                onCancel={toggleAllModal}
                width="90%"
                style={{ maxWidth: '1000px', top: '5vh' }}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', padding: '16px' }}
                centered={false}>
                {allLoading ? (
                    <div className="flex justify-center items-center" style={{minHeight: '300px'}}>
                        <FaceAnalyzingAnimComponent
                            image_url={selectedFile['url']}
                            process_name={currentProcess}
                        />
                    </div>
                ) : (
                    <>
                        <Carousel
                            infinite={false}
                            draggable={true}
                            ref={slider}
                        >
                            <div className="h-full flex flex-col justify-center">
                                <div style={contentStyle}>
                                    <h3>{t("wrinkles_analysis")}</h3>
                                    {renderWrinklesAnalysisResult()}
                                </div>
                            </div>
                            <div className="h-full flex-col justify-center">
                                <div style={contentStyle}>
                                    <h3>{t("skin_lesion_analysis")}</h3>
                                    {renderSkinLesionAnalysisResult()}
                                </div>
                            </div>

                        </Carousel>
                    </>
                )}
                {!allLoading && (
                    <div className="flex justify-end mt-4 gap-3">
                        <Button type="primary" onClick={() => slider?.current?.prev()}>
                            {t("previous")}
                        </Button>
                        <Button type="primary" onClick={() => slider?.current?.next()}>
                            {t("next")}
                        </Button>
                        <Button type="primary" onClick={toggleAllModal}>
                            {t("close")}
                        </Button>
                    </div>
                )}
            </Modal>
        </DashboardWrapper>
    );
}

const WrinkleWrapper = styled.div`
    * {
        color: black;
    }
`

const LesionWrapper = styled.div`
    * {
        color: black;
    }

    table {
        border-collapse: collapse;
    }

    table td, table th {
        border: 1px solid #e2e8f0;
    }
`

const StyledImageField = styled.div`
    cursor: pointer;
    border-radius: 12px;
    overflow: hidden;
    background: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    }

    .image-container {
        position: relative;
        width: 100%;
        padding-top: 100%; /* 1:1 Aspect Ratio */
        overflow: hidden;
        background: #f5f5f5;
        flex-shrink: 0;

        img, video {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.3s ease;
        }

        &.video-container video {
            pointer-events: none;
        }

        .overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 0.3s ease;
        }

        .overlay-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            color: white;
            font-weight: 500;
            
            .play-icon {
                font-size: 32px;
                color: white;
            }

            .view-text {
                font-size: 14px;
                text-transform: uppercase;
                letter-spacing: 1px;
            }
        }

        .video-badge {
            position: absolute;
            top: 8px;
            right: 8px;
            background: rgba(0, 0, 0, 0.7);
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 10px;
            font-weight: 600;
            letter-spacing: 0.5px;
        }
    }

    &:hover .image-container {
        img, video {
            transform: scale(1.05);
        }

        .overlay {
            opacity: 1;
        }
    }

    .file-info {
        padding: 12px;
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        
        .file-name {
            margin: 0;
            font-size: 14px;
            font-weight: 500;
            color: #333;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .file-date {
            margin: 4px 0 0 0;
            font-size: 12px;
            color: #888;
        }
    }
`

const StyledModalContent = styled.div`
    display: flex;
    flex-direction: column;
    background: #f8f9fa;
    border-radius: 8px;
    overflow: hidden;

    .modal-media-container {
        background: #000;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 400px;
        max-height: 70vh;
        position: relative;

        img, video {
            width: 100%;
            height: 100%;
            max-height: 70vh;
            object-fit: contain;
            display: block;
        }

        video {
            background: #000;
        }
    }

    .modal-info-section {
        padding: 24px;
        background: white;
    }

    .modal-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;

        .analyze-btn {
            height: 44px;
            padding: 0 32px;
            font-size: 16px;
            font-weight: 500;
        }

        .action-buttons {
            display: flex;
            gap: 12px;
            flex-wrap: wrap;

            button {
                height: 44px;
                display: flex;
                align-items: center;
                gap: 8px;
            }
        }
    }

    @media (max-width: 768px) {
        .modal-media-container {
            min-height: 300px;
            max-height: 50vh;
        }

        .modal-info-section {
            padding: 16px;
        }

        .modal-actions {
            flex-direction: column;
            align-items: stretch;

            .analyze-btn,
            .action-buttons {
                width: 100%;
            }

            .action-buttons {
                button {
                    flex: 1;
                }
            }
        }
    }
`

const contentStyle = {
    // margin: '0 32px',
};
export default PatientsCabinetPage;